import { Alert, Box, CircularProgress, TextField, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { keys } from "../../api/queryKeys.ts";
import type { MicroAppQueryResult, MicroAppView } from "../../api/client.ts";
import { DeclarativeViews } from "../../components/micro-apps/MicroAppViews.tsx";
import { MAX_DOCUMENT_REMINTS, SandboxFrame } from "../../components/micro-apps/SandboxFrame.tsx";

/**
 * TV-shared micro apps: `dash.sxq.no/<6-digit code>`, no login.
 *
 * Rendered by `main.tsx` instead of the whole Vayken app on the dash host (no
 * auth, org or realtime providers — there is no session there), and mounted
 * under `/tv` in `App.tsx` in dev builds only. Talks only to the public
 * `/api/v1/tv` endpoint, with plain `fetch`: the shared api client redirects
 * to /login on a 401, which is never the right answer on a TV.
 */

const CODE_RE = /^\d{6,8}$/;  // legacy 6-digit still valid; new codes are 8
const LAST_CODE_KEY = "hive:tv:last-code";

// A hard reload on this cadence drops whatever a long-running TV browser has
// accumulated and picks up Vayken frontend deploys. New *app* versions arrive
// sooner, on the next data poll.
const RELOAD_EVERY_MS = 60 * 60 * 1000;
// Before reloading, make sure the page can actually be fetched again: a reload
// while offline leaves the TV on the browser's error page for good.
const RELOAD_RETRY_MS = 60 * 1000;
// A dashboard that was showing and now answers 404 — disabled, unpublished or
// unshared, which the server deliberately does not tell apart — is re-checked
// this often. Slow on purpose: a check of a dead code counts against the
// network's bad-code budget, which every TV behind the same address shares.
const UNAVAILABLE_POLL_MS = 5 * 60 * 1000;

interface TvRender {
  slug: string;
  title: string;
  version: number;
  views: MicroAppView[];
  refresh_seconds: number | null;
  data: Record<string, MicroAppQueryResult>;
  has_client_code: boolean;
  document_url: string | null;
  fetched_at: string;
}

class TvError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function fetchTvRender(code: string, knownVersion: number | null): Promise<TvRender> {
  const qs = knownVersion != null ? `?known_version=${knownVersion}` : "";
  const response = await fetch(`/api/v1/tv/${encodeURIComponent(code)}/render${qs}`, {
    method: "POST",
    credentials: "omit",
  });
  if (!response.ok) {
    let message = `Error ${response.status}`;
    try {
      const body = await response.json();
      if (typeof body?.error === "string") message = body.error;
    } catch {
      // not JSON
    }
    throw new TvError(response.status, message);
  }
  return response.json();
}

function readLastCode(): string | null {
  try {
    return localStorage.getItem(LAST_CODE_KEY);
  } catch {
    return null;
  }
}

function writeLastCode(code: string | null) {
  try {
    if (code) localStorage.setItem(LAST_CODE_KEY, code);
    else localStorage.removeItem(LAST_CODE_KEY);
  } catch {
    // storage disabled; the TV just won't remember its code
  }
}

function FullScreenMessage({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 3,
        p: 4,
        bgcolor: "background.default",
      }}
    >
      {children}
    </Box>
  );
}

function TvCodeEntry() {
  const navigate = useNavigate();
  const location = useLocation();
  const error = (location.state as { error?: string } | null)?.error ?? null;
  const [value, setValue] = useState("");

  // A TV that restarts on the bare host goes straight back to its dashboard —
  // unless it was just sent here because that code stopped working.
  useEffect(() => {
    const last = readLastCode();
    if (!error && last && CODE_RE.test(last)) navigate(last, { replace: true });
  }, [error, navigate]);

  const onChange = (raw: string) => {
    const digits = raw.replace(/\D/g, "").slice(0, 8);
    setValue(digits);
    if (digits.length === 6) navigate(digits);
  };

  return (
    <FullScreenMessage>
      <Typography sx={{ fontSize: 40, fontWeight: 600 }}>
        Enter dashboard code
      </Typography>
      <TextField
        autoFocus
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="000000"
        slotProps={{
          htmlInput: {
            inputMode: "numeric",
            autoComplete: "off",
            maxLength: 6,
            "aria-label": "Dashboard code",
            style: {
              fontSize: 64,
              fontFamily: "monospace",
              letterSpacing: "0.3em",
              textAlign: "center",
              width: "9ch",
            },
          },
        }}
      />
      {error ? (
        <Alert severity="error" sx={{ fontSize: 20 }}>
          {error}
        </Alert>
      ) : (
        <Typography color="text.secondary">
          Find the code in Vayken under Settings → Micro Apps → Share to TV.
        </Typography>
      )}
    </FullScreenMessage>
  );
}

/** Keep the screen awake, and reload the page every hour once it is reachable. */
function useTvScreen() {
  useEffect(() => {
    let lock: WakeLockSentinel | null = null;
    const acquire = async () => {
      try {
        if ("wakeLock" in navigator && document.visibilityState === "visible") {
          lock = await navigator.wakeLock.request("screen");
        }
      } catch {
        // unsupported or refused — the TV's own settings decide
      }
    };
    void acquire();
    // A wake lock is dropped whenever the page is hidden; take it back.
    const onVisible = () => {
      if (document.visibilityState === "visible") void acquire();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      void lock?.release().catch(() => undefined);
    };
  }, []);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const reloadWhenReachable = async () => {
      try {
        const r = await fetch(window.location.pathname, { cache: "no-store" });
        if (r.ok) {
          window.location.reload();
          return;
        }
      } catch {
        // offline; try again shortly
      }
      timer = setTimeout(reloadWhenReachable, RELOAD_RETRY_MS);
    };
    timer = setTimeout(reloadWhenReachable, RELOAD_EVERY_MS);
    return () => clearTimeout(timer);
  }, []);
}

function TvDashboard() {
  const { code = "" } = useParams();
  const navigate = useNavigate();
  const valid = CODE_RE.test(code);
  useTvScreen();

  // What the sandbox frame holds, as on the host page: sending its version
  // keeps the frame and pushes fresh data in; a newly published version comes
  // back with a new document and the frame swaps.
  const mounted = useRef<{ version: number; url: string } | null>(null);
  // Whether this code has rendered at all. A 404 before that is a wrong code
  // (back to entry); after it, the dashboard went away and may come back.
  const everShown = useRef(false);

  const { data, error, refetch } = useQuery({
    queryKey: keys.tvShare.byCode(code),
    queryFn: () => fetchTvRender(code, mounted.current?.version ?? null),
    enabled: valid,
    refetchInterval: (query) => {
      const status = (query.state.error as TvError | null)?.status;
      if (status === 404) return everShown.current ? UNAVAILABLE_POLL_MS : false;
      return (query.state.data?.refresh_seconds ?? 300) * 1000;
    },
    refetchIntervalInBackground: true,
    retry: (failures, err) => (err as TvError).status === undefined && failures < 3,
    // Single-use document handles must never be replayed from a cache.
    gcTime: 0,
    meta: { suppressErrorToast: true },
  });

  const status = (error as TvError | null)?.status;
  useEffect(() => {
    if (!valid || (status === 404 && !everShown.current)) {
      writeLastCode(null);
      navigate("..", {
        relative: "path",
        replace: true,
        state: {
          error: valid
            ? "Unknown code, or sharing was turned off."
            : "Codes are 6 digits.",
        },
      });
    }
  }, [valid, status, navigate]);

  useEffect(() => {
    if (data) writeLastCode(code);
  }, [data, code]);

  // The frame never announced itself, so its document is gone. Forget it so
  // the next fetch mints a new one; refetch at once while the budget lasts,
  // otherwise the regular poll heals it — a TV has nobody to press reload.
  const remintsLeft = useRef(MAX_DOCUMENT_REMINTS);
  const handleDocumentLost = useCallback(() => {
    mounted.current = null;
    if (remintsLeft.current <= 0) return;
    remintsLeft.current -= 1;
    void refetch();
  }, [refetch]);

  if (data) everShown.current = true;

  // Never keep showing the last render of a dashboard that was taken down.
  if (status === 404 && everShown.current) {
    return (
      <FullScreenMessage>
        <Typography variant="h5" color="text.secondary">
          This dashboard isn’t available right now.
        </Typography>
      </FullScreenMessage>
    );
  }

  if (!data) {
    if (status === 429) {
      return (
        <FullScreenMessage>
          <Alert severity="warning" sx={{ fontSize: 20 }}>
            Too many wrong codes from this network. Try again in a few minutes.
          </Alert>
        </FullScreenMessage>
      );
    }
    if (error && status !== 404) {
      return (
        <FullScreenMessage>
          <Typography variant="h5" color="text.secondary">
            {(error as TvError).message} — retrying…
          </Typography>
        </FullScreenMessage>
      );
    }
    return (
      <FullScreenMessage>
        <CircularProgress />
      </FullScreenMessage>
    );
  }

  if (data.document_url) {
    mounted.current = { version: data.version, url: data.document_url };
  }
  const documentUrl = mounted.current?.url ?? null;

  // A failed refresh keeps the last good render on screen.
  if (documentUrl) {
    return (
      <Box sx={{ height: "100vh", overflow: "hidden", bgcolor: "background.default" }}>
        <SandboxFrame
          src={documentUrl}
          title={data.title}
          slug={data.slug}
          version={data.version}
          fetchedAt={data.fetched_at}
          data={data.data}
          onDocumentLost={handleDocumentLost}
          fill
        />
      </Box>
    );
  }
  if (data.has_client_code) {
    return (
      <FullScreenMessage>
        <CircularProgress />
      </FullScreenMessage>
    );
  }
  return (
    <Box sx={{ minHeight: "100vh", p: 3, bgcolor: "background.default" }}>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 3 }}>
        {data.title}
      </Typography>
      <DeclarativeViews views={data.views} data={data.data} />
    </Box>
  );
}

function TvApp() {
  return (
    <Routes>
      <Route index element={<TvCodeEntry />} />
      <Route path=":code" element={<TvDashboard />} />
    </Routes>
  );
}

export default TvApp;
